import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { getSocket } from "../utils/socket";
import Navbar from "../components/Navbar";
import ChatWidgetParticipants from "../components/ChatWidgetParticipants";
import ProfileStats from "../components/ProfileStats";
import UserSearch from "../components/UserSearch";
import UserList from "../components/UserList";
import SendGift from "./SendGift";

const LikePage = () => {
  const { user: authUser, token, fetchWithRetry } = useAuth();
  const [likes, setLikes] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingLike, setIsLoadingLike] = useState(false);
  const [likeStates, setLikeStates] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("last_activity");
  const [showPhotoGallery, setShowPhotoGallery] = useState(null);
  const [chatUsers, setChatUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeTab, setActiveTab] = useState("likes");
  const itemsPerPage = 18;

  const [genderFilter, setGenderFilter] = useState("");
  const [minAgeFilter, setMinAgeFilter] = useState("");
  const [maxAgeFilter, setMaxAgeFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");

  const [coinBalance, setCoinBalance] = useState(0);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [isTooltipFading, setIsTooltipFading] = useState(false);

  const showTooltip = useCallback((message, type = "success") => {
    setTooltip({ message, type });
    setTimeout(() => {
      setIsTooltipFading(true);
      setTimeout(() => {
        setTooltip(null);
        setIsTooltipFading(false);
      }, 300);
    }, 1500);
  }, []);

  const fetchLikes = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        setError(null);
        const query = new URLSearchParams({
          page: page,
          limit: itemsPerPage,
          search: searchQuery,
          sort: sortBy,
          gender: genderFilter,
          minAge: minAgeFilter,
          maxAge: maxAgeFilter,
          country: countryFilter,
          state: stateFilter,
          city: cityFilter,
        }).toString();
        const res = await fetchWithRetry({
          method: "get",
          url: `/api/users/likes?${query}`,
        });
        const fetchedLikes = res.data.matches || [];
        const validLikes = fetchedLikes
          .filter((like) => {
            const displayUser = String(like.userId._id) === String(authUser._id) ? like.likedUserId : like.userId;
            if (!displayUser._id || !/^[0-9a-fA-F]{24}$/.test(displayUser._id)) {
              console.warn(`Like thiếu user _id:`, displayUser);
              return false;
            }
            if (!displayUser.membershipId) {
              console.warn(`Like ${displayUser._id} thiếu membershipId`);
              return false;
            }
            return like.status === "matched";
          })
          .map((like) => {
            const displayUser = String(like.userId._id) === String(authUser._id) ? like.likedUserId : like.userId;
            return {
              ...displayUser,
              primaryPhoto: displayUser.primaryPhoto || { photoUrl: "", caption: "" },
              mainImage: displayUser.mainImage || "",
              avatar: displayUser.avatar || "",
              likeId: like._id,
              isLiked: true,
              isSentByCurrentUser: String(like.userId._id) === String(authUser._id),
            };
          });
        setLikes(validLikes);
        setTotalPages(res.data.totalPages || 1);
        setTotalUsers(res.data.total || validLikes.length);
        setCurrentPage(page);
        const updatedLikeStates = validLikes.reduce((acc, like) => {
          acc[like._id] = true;
          return acc;
        }, {});
        setLikeStates(updatedLikeStates);
        if (validLikes.length === 0) {
          setError("Bạn chưa có lượt thích nào hoặc dữ liệu không hợp lệ.");
        }
      } catch (error) {
        console.error("Lỗi khi lấy danh sách Like:", error.response || error);
        setError(
          error.response?.status === 401
            ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
            : error.response?.status === 403
            ? "Bạn không có quyền truy cập danh sách Like!"
            : error.response?.data?.message || "Không thể tải danh sách Like."
        );
        setLikes([]);
        setLikeStates({});
      } finally {
        setLoading(false);
      }
    },
    [
      searchQuery,
      sortBy,
      genderFilter,
      minAgeFilter,
      maxAgeFilter,
      countryFilter,
      stateFilter,
      cityFilter,
      fetchWithRetry,
      authUser,
    ]
  );

  const fetchMatches = useCallback(
    async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetchWithRetry({
          method: "get",
          url: "/api/users/matches",
        });
        const fetchedMatches = res.data.matches || [];
        const validMatches = fetchedMatches
          .filter((match) => {
            if (match.status !== "pending") return false;
            const likedUserId = match.likedUserId?._id ? String(match.likedUserId._id) : String(match.likedUserId);
            if (likedUserId !== String(authUser._id)) return false;

            const displayUser = match.userId;
            if (!displayUser?._id || !/^[0-9a-fA-F]{24}$/.test(displayUser._id)) {
              console.warn(`Match thiếu user _id:`, displayUser);
              return false;
            }
            if (!match._id || !/^[0-9a-fA-F]{24}$/.test(match._id)) {
              console.warn(`Match ${match._id} có ID không hợp lệ:`, match);
              return false;
            }
            if (!displayUser?.membershipId) {
              console.warn(`Match ${displayUser._id} thiếu membershipId`);
              return false;
            }
            return true;
          })
          .map((match) => ({
            ...match,
            userId: {
              ...match.userId,
              _id: String(match.userId._id),
              primaryPhoto: match.userId.primaryPhoto || { photoUrl: "", caption: "" },
              avatar: match.userId.avatar || "",
              membershipId: match.userId.membershipId || "",
            },
            likedUserId: {
              ...match.likedUserId,
              _id: String(match.likedUserId._id),
              primaryPhoto: match.likedUserId.primaryPhoto || { photoUrl: "", caption: "" },
              avatar: match.likedUserId.avatar || "",
              membershipId: match.likedUserId.membershipId || "",
            },
          }));
        setMatches(validMatches);
        if (validMatches.length === 0) {
          setError("Không có yêu cầu Match nào đang chờ chấp nhận.");
        }
      } catch (error) {
        console.error("Lỗi khi lấy danh sách Match:", error.response || error);
        setError(
          error.response?.status === 401
            ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
            : error.response?.status === 403
            ? "Bạn không có quyền truy cập danh sách Match!"
            : error.response?.data?.message || "Không thể tải danh sách Match."
        );
        setMatches([]);
      } finally {
        setLoading(false);
      }
    },
    [fetchWithRetry, authUser]
  );

  const fetchNotifications = useCallback(
    async () => {
      try {
        const res = await fetchWithRetry({
          method: "get",
          url: "/api/users/notifications",
        });
        setNotifications(res.data);
        const unread = res.data.filter((n) => !n.read).length;
        setUnreadCount(unread);
      } catch (error) {
        console.error("Lỗi khi lấy thông báo:", error.response || error);
        setError(
          error.response?.status === 401
            ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
            : error.response?.data?.message || "Không thể tải thông báo."
        );
      }
    },
    [fetchWithRetry]
  );

  const handleLike = async (likedUserId) => {
    if (isLoadingLike) return;
    setIsLoadingLike(true);
    const originalLikeState = likeStates[likedUserId];
    try {
      if (!authUser || !authUser._id) {
        throw new Error("Người dùng chưa được tải, vui lòng thử lại!");
      }
      if (!likedUserId || typeof likedUserId !== "string" || !/^[0-9a-fA-F]{24}$/.test(likedUserId)) {
        throw new Error("ID người dùng không hợp lệ!");
      }
      const isAlreadyLiked = likeStates[likedUserId];
      if (isAlreadyLiked) {
        throw new Error("Bạn đã Like người dùng này!");
      }
      setLikeStates((prev) => ({ ...prev, [likedUserId]: true }));
      const response = await fetchWithRetry({
        method: "put",
        url: "/api/users/like",
        data: { likedUserId: String(likedUserId) },
      });
      showTooltip(response.data.message || "Thích thành công!", "success");
      await fetchLikes(currentPage);
      await fetchMatches();
      await fetchNotifications();
    } catch (error) {
      console.error("Lỗi khi gửi Like:", error.response || error);
      setLikeStates((prev) => ({ ...prev, [likedUserId]: originalLikeState }));
      showTooltip(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.status === 403
          ? "Một trong hai người dùng bị khóa!"
          : error.response?.data?.message || error.message || "Lỗi khi gửi thích",
        "error"
      );
    } finally {
      setIsLoadingLike(false);
    }
  };

  const handleUnlike = async (likedUserId) => {
    if (isLoadingLike) return;
    setIsLoadingLike(true);
    const originalLikeState = likeStates[likedUserId];
    try {
      if (!authUser || !authUser._id) {
        throw new Error("Người dùng chưa được tải, vui lòng thử lại!");
      }
      if (!likedUserId || typeof likedUserId !== "string" || !/^[0-9a-fA-F]{24}$/.test(likedUserId)) {
        throw new Error("ID người dùng không hợp lệ!");
      }
      const isLiked = likeStates[likedUserId];
      if (!isLiked) {
        throw new Error("Bạn chưa Like người dùng này!");
      }

      const likesResponse = await fetchWithRetry({
        method: "get",
        url: "/api/users/likes",
      });
      const fetchedLikes = likesResponse.data.matches || [];
      const hasLiked = fetchedLikes.some(
        (like) =>
          (String(like.userId._id) === String(authUser._id) &&
           String(like.likedUserId._id) === String(likedUserId)) ||
          (String(like.userId._id) === String(likedUserId) &&
           String(like.likedUserId._id) === String(authUser._id))
      );
      if (!hasLiked) {
        setLikeStates((prev) => ({ ...prev, [likedUserId]: false }));
        setLikes((prev) => prev.filter((like) => String(like._id) !== String(likedUserId)));
        showTooltip("Bạn chưa Like người dùng này hoặc Like đã bị thu hồi!", "info");
        await fetchLikes(currentPage);
        return;
      }

      setLikeStates((prev) => ({ ...prev, [likedUserId]: false }));
      setLikes((prev) => prev.filter((like) => String(like._id) !== String(likedUserId)));
      const response = await fetchWithRetry({
        method: "put",
        url: "/api/users/cancel-match",
        data: { likedUserId },
      });

      const socket = getSocket();
      socket.emit("likeRemoved", {
        userId: authUser._id,
        likedUserId,
      });

      showTooltip(response.data.message || "Bỏ thích thành công!", "success");
      await fetchLikes(currentPage);
      await fetchMatches();
      await fetchNotifications();
    } catch (error) {
      console.error("Lỗi khi bỏ thích:", error.response || error);
      setLikeStates((prev) => ({ ...prev, [likedUserId]: originalLikeState }));
      const errorMessage =
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.status === 403
          ? "Một trong hai người dùng bị khóa!"
          : error.response?.status === 404
          ? "Không tìm thấy Match hoặc Match không ở trạng thái matched!"
          : error.response?.data?.message || error.message || "Lỗi khi bỏ thích";
      if (error.response?.data?.message === "Bạn chưa Like người dùng này!") {
        setLikeStates((prev) => ({ ...prev, [likedUserId]: false }));
        setLikes((prev) => prev.filter((like) => String(like._id) !== String(likedUserId)));
        showTooltip("Like đã được thu hồi trước đó!", "info");
        await fetchLikes(currentPage);
      } else {
        showTooltip(errorMessage, "error");
      }
    } finally {
      setIsLoadingLike(false);
    }
  };

  const handleRespondMatchRequest = async (matchId, action) => {
    if (isLoadingLike) return;
    setIsLoadingLike(true);
    try {
      if (!authUser || !authUser._id) {
        throw new Error("Người dùng chưa được tải, vui lòng thử lại!");
      }
      if (!matchId || !/^[0-9a-fA-F]{24}$/.test(matchId)) {
        throw new Error("ID Match không hợp lệ!");
      }

      const response = await fetchWithRetry({
        method: "put",
        url: `/api/users/match-request/${matchId}`,
        data: { action },
      });

      if (response.status === 200) {
        showTooltip(
          response.data.message ||
            (action === "accept"
              ? "Chấp nhận Match thành công!"
              : "Từ chối Match thành công!"),
          "success"
        );

        setMatches((prev) => prev.filter((m) => String(m._id) !== String(matchId)));
        if (action === "accept") {
          await fetchLikes(currentPage);
        }
        await fetchMatches();
        await fetchNotifications();
      } else {
        throw new Error(response.data.message || "Phản hồi từ server không hợp lệ!");
      }
    } catch (error) {
      console.error("Lỗi khi phản hồi Match:", error);
      const errorMessage =
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.status === 403
          ? "Bạn không có quyền xử lý Match này!"
          : error.response?.status === 404
          ? "Match không tồn tại hoặc đã được xử lý!"
          : error.response?.data?.message || error.message || "Lỗi khi phản hồi Match";
      showTooltip(errorMessage, "error");
      await fetchMatches();
    } finally {
      setIsLoadingLike(false);
    }
  };

  const handleToggleNotifications = async (notificationId, action) => {
    if (!token) {
      showTooltip("Không có token, vui lòng đăng nhập lại!", "error");
      return;
    }

    if (!action || !notificationId) {
      return;
    }

    try {
      let response;
      if (action === "read") {
        response = await fetchWithRetry({
          method: "put",
          url: `/api/users/notifications/${notificationId}/read`,
          data: {},
        });
      } else if (action === "delete") {
        response = await fetchWithRetry({
          method: "delete",
          url: `/api/users/notifications/${notificationId}`,
        });
      } else {
        console.warn(`Hành động không hợp lệ: ${action}`);
        return;
      }

      if (response.status === 200) {
        if (action === "read") {
          setNotifications((prev) =>
            prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
          );
          setUnreadCount((prev) => prev - 1);
        } else if (action === "delete") {
          setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
          setUnreadCount((prev) => {
            const deletedNotification = notifications.find((n) => n._id === notificationId);
            return deletedNotification && !deletedNotification.read ? prev - 1 : prev;
          });
        }
      }
    } catch (error) {
      console.error("Lỗi khi xử lý thông báo:", error.response || error);
      showTooltip(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.data?.message || `Lỗi khi ${action} thông báo!`,
        "error"
      );
    }
  };

  const handleSearch = () => {
    if (activeTab === "likes") {
      setCurrentPage(1);
      fetchLikes(1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchLikes(page);
  };

  const handleSendGift = (userId) => {
    const selectedUser = likes.find((user) => user._id === userId) || matches.find((m) => {
      const displayUser = String(m.userId._id) === String(authUser._id) ? m.likedUserId : m.userId;
      return displayUser._id === userId;
    });
    if (selectedUser) {
      const displayUser = selectedUser._id ? selectedUser : (String(selectedUser.userId._id) === String(authUser._id) ? selectedUser.likedUserId : selectedUser.userId);
      if (!displayUser._id || !displayUser.membershipId) {
        console.warn(`Người dùng ${userId} thiếu _id hoặc membershipId:`, displayUser);
        showTooltip("Không thể gửi quà: Dữ liệu người dùng không đầy đủ!", "error");
        return;
      }
      setSelectedUserId({
        membershipId: displayUser.membershipId,
        id: displayUser._id,
      });
      setShowGiftModal(true);
    } else {
      showTooltip("Không tìm thấy người dùng!", "error");
    }
  };

  const handleGiftSent = useCallback(
    async (gift) => {
      if (!selectedUserId?.id) {
        showTooltip("Không thể gửi quà: Người nhận không hợp lệ!", "error");
        return;
      }
      if (!gift || !gift._id) {
        showTooltip("Không thể gửi quà: Quà tặng không hợp lệ!", "error");
        return;
      }

      try {
        const response = await fetchWithRetry({
          method: "post",
          url: "/api/users/send-gift",
          data: { receiverId: selectedUserId.id, giftId: gift._id },
        });

        if (response.status === 200) {
          setShowGiftModal(false);
          setCoinBalance(response.data.coinBalance || coinBalance);
          showTooltip(`Đã gửi quà "${gift.name}" thành công!`, "success");
          await fetchNotifications();
        } else {
          throw new Error(response.data.message || "Phản hồi từ server không hợp lệ!");
        }
      } catch (error) {
        console.error("Lỗi khi gửi quà tặng:", error.response || error);
        showTooltip(
          error.response?.status === 401
            ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
            : error.response?.status === 403
            ? "Bạn không có quyền gửi quà!"
            : error.response?.data?.message || error.message || "Lỗi khi gửi quà tặng!",
          "error"
        );
      }
    },
    [fetchWithRetry, selectedUserId, showTooltip, fetchNotifications, coinBalance]
  );

  const handleViewPhotos = (user) => {
    if (!user._id || !user.membershipId) {
      console.warn(`Người dùng thiếu _id hoặc membershipId:`, user);
      showTooltip("Không thể xem ảnh: Dữ liệu người dùng không đầy đủ!", "error");
      return;
    }
    if (!user.photos || user.photos.length === 0) {
      showTooltip("Người dùng này chưa có ảnh nào để hiển thị!", "info");
      return;
    }
    setShowPhotoGallery(user);
  };

  const handleOpenChat = (userId) => {
    if (!userId || !authUser || !authUser._id) {
      showTooltip("Không thể mở chat: Dữ liệu người dùng không hợp lệ!", "error");
      console.error("Dữ liệu người dùng không hợp lệ", { userId, authUser });
      return;
    }
    const selectedUser = likes.find((u) => u._id === userId) || matches.find((m) => {
      const displayUser = String(m.userId._id) === String(authUser._id) ? m.likedUserId : m.userId;
      return displayUser._id === userId;
    });
    if (!selectedUser) {
      showTooltip("Người dùng không tồn tại trong danh sách Like hoặc Match!", "error");
      console.error(`Không tìm thấy người dùng với _id ${userId} trong danh sách Like hoặc Match`);
      return;
    }
    const displayUser = selectedUser._id ? selectedUser : (String(selectedUser.userId._id) === String(authUser._id) ? selectedUser.likedUserId : selectedUser.userId);
    if (!displayUser._id || !displayUser.membershipId) {
      showTooltip("Dữ liệu người dùng không đầy đủ: Thiếu ID hoặc membershipId!", "error");
      console.error(`Dữ liệu người dùng không hợp lệ cho _id ${userId}`, displayUser);
      return;
    }
    setChatUsers((prev) => [...new Set([...prev, displayUser._id])]);
  };

  useEffect(() => {
    if (authUser) {
      const fetchData = async () => {
        if (activeTab === "likes") {
          await fetchLikes(currentPage);
        } else {
          await fetchMatches();
        }
        await fetchNotifications();
      };

      fetchData();

      const pollInterval = setInterval(fetchNotifications, 10000);

      const socket = getSocket();
      if (!socket.connected) {
        socket.connect();
      }

      socket.on("connect", () => {
        console.log("Kết nối WebSocket thành công:", socket.id);
        socket.emit("joinRoom", authUser._id);
      });

      socket.on("connect_error", (error) => {
        console.error("Lỗi kết nối WebSocket:", error);
        showTooltip("Không thể kết nối WebSocket, một số cập nhật có thể bị chậm!", "error");
      });

      socket.on("notification", (notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);
        if (notification.type === "like" || notification.type === "match") {
          if (activeTab === "matches") {
            fetchMatches();
          } else {
            fetchLikes(currentPage);
          }
        }
      });

      socket.on("likeSent", (data) => {
        if (String(data.userId) === String(authUser._id)) {
          setLikeStates((prev) => ({ ...prev, [data.likedUserId]: true }));
        }
      });

      socket.on("likeReceived", (data) => {
        if (String(data.likedUserId) === String(authUser._id)) {
          fetchMatches();
        }
      });

      socket.on("likeRemoved", (data) => {
        if (String(data.userId) === String(authUser._id) || String(data.likedUserId) === String(authUser._id)) {
          const removedUserId = String(data.userId) === String(authUser._id) ? data.likedUserId : data.userId;
          setLikeStates((prev) => ({ ...prev, [removedUserId]: false }));
          setLikes((prev) => prev.filter((like) => String(like._id) !== String(removedUserId)));
          fetchMatches();
          fetchLikes(currentPage);
        }
      });

      socket.on("matchUpdate", (data) => {
        if (data.status === "matched") {
          setMatches((prev) => prev.filter((m) => String(m._id) !== String(data.matchId)));
          fetchLikes(currentPage);
          fetchMatches();
        } else if (data.status === "rejected") {
          setMatches((prev) => prev.filter((m) => String(m._id) !== String(data.matchId)));
          fetchMatches();
        }
      });

      socket.on("coinBalanceUpdate", (data) => {
        setCoinBalance(data.coinBalance);
        showTooltip(`Số dư coin của bạn đã được cập nhật: ${data.coinBalance} coin`, "success");
      });

      return () => {
        clearInterval(pollInterval);
        socket.off("connect");
        socket.off("connect_error");
        socket.off("notification");
        socket.off("likeSent");
        socket.off("likeReceived");
        socket.off("likeRemoved");
        socket.off("matchUpdate");
        socket.off("coinBalanceUpdate");
      };
    }
  }, [
    authUser,
    token,
    activeTab,
    currentPage,
    fetchLikes,
    fetchMatches,
    fetchNotifications,
    showTooltip,
  ]);

  if (!authUser) {
    return <p className="text-center text-gray-600">Đang tải...</p>;
  }

  const displayedMatches = matches.map((match) => {
    if (!match.userId._id || !/^[0-9a-fA-F]{24}$/.test(match.userId._id)) {
      console.warn(`Match ${match._id} thiếu user _id:`, match.userId);
      return null;
    }
    if (!match.userId.membershipId) {
      console.warn(`Match ${match.userId._id} thiếu membershipId`);
      return null;
    }
    return {
      ...match.userId,
      primaryPhoto: match.userId.primaryPhoto || { photoUrl: "", caption: "" },
      mainImage: match.userId.mainImage || "",
      avatar: match.userId.avatar || "",
      matchId: match._id,
      isMatchRequestPending: true,
      isSentByCurrentUser: false,
    };
  }).filter((match) => match !== null);

  return (
    <div className="font-sans min-h-screen bg-gray-100">
      <Navbar
        unreadCount={unreadCount}
        unreadNotificationCount={unreadCount}
        notifications={notifications}
        onToggleNotifications={handleToggleNotifications}
      />

      <div className="pt-20 max-w-[1600px] mx-auto p-8">
        <ProfileStats authUser={authUser} coinBalance={coinBalance} />

        <div className="mb-4">
          <p className="text-gray-700">
            Bạn có{" "}
            <span className="font-semibold">{matches.length}</span>{" "}
            yêu cầu Match đang chờ chấp nhận.
          </p>
        </div>

        <div className="flex border-b mb-6">
          <button
            onClick={() => {
              setActiveTab("likes");
              setCurrentPage(1);
              fetchLikes(1);
            }}
            className={`px-4 py-2 font-medium ${
              activeTab === "likes"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            Lượt thích ({likes.length})
          </button>
          <button
            onClick={() => {
              setActiveTab("matches");
              fetchMatches();
            }}
            className={`px-4 py-2 font-medium ${
              activeTab === "matches"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            Match ({matches.length})
          </button>
        </div>

        {activeTab === "likes" && (
          <UserSearch
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            genderFilter={genderFilter}
            setGenderFilter={setGenderFilter}
            minAgeFilter={minAgeFilter}
            setMinAgeFilter={setMinAgeFilter}
            maxAgeFilter={maxAgeFilter}
            setMaxAgeFilter={setMaxAgeFilter}
            countryFilter={countryFilter}
            setCountryFilter={setCountryFilter}
            stateFilter={stateFilter}
            setStateFilter={setStateFilter}
            cityFilter={cityFilter}
            setCityFilter={setCityFilter}
            sortBy={sortBy}
            setSortBy={setSortBy}
            handleSearch={handleSearch}
          />
        )}

        {loading ? (
          <p className="text-center text-gray-500">
            Đang tải danh sách {activeTab === "likes" ? "lượt thích" : "Match"}...
          </p>
        ) : error ? (
          <p className="text-red-500 text-center mb-4">{error}</p>
        ) : activeTab === "likes" ? (
          <UserList
            filteredUsers={likes}
            authUser={authUser}
            likeStates={likeStates}
            friendRequestStates={{}}
            friendRequests={[]}
            matches={matches}
            friends={[]}
            isLoadingLike={isLoadingLike}
            isLoadingFriend={false}
            handleLike={handleLike}
            handleUnlike={handleUnlike}
            sendFriendRequest={() => {}}
            cancelFriendRequest={() => {}}
            handleRespondFriendRequest={() => {}}
            handleRespondMatchRequest={handleRespondMatchRequest}
            handleOpenChat={handleOpenChat}
            handleSendGift={handleSendGift}
            handleViewPhotos={handleViewPhotos}
            handleRemoveFriend={() => {}}
            currentPage={currentPage}
            totalPages={totalPages}
            totalUsers={totalUsers}
            handlePageChange={handlePageChange}
            showTooltip={showTooltip}
            loading={loading}
            skeletonCount={18}
            isFriendList={false}
            isFriendRequestList={false}
            isMatchRequestList={false}
          />
        ) : (
          <UserList
            filteredUsers={displayedMatches}
            authUser={authUser}
            likeStates={{}}
            friendRequestStates={{}}
            friendRequests={[]}
            matches={matches}
            friends={[]}
            isLoadingLike={isLoadingLike}
            isLoadingFriend={false}
            handleLike={handleLike}
            handleUnlike={handleUnlike}
            sendFriendRequest={() => {}}
            cancelFriendRequest={() => {}}
            handleRespondFriendRequest={() => {}}
            handleRespondMatchRequest={handleRespondMatchRequest}
            handleOpenChat={handleOpenChat}
            handleSendGift={handleSendGift}
            handleViewPhotos={handleViewPhotos}
            handleRemoveFriend={() => {}}
            currentPage={1}
            totalPages={1}
            totalUsers={matches.length}
            handlePageChange={() => {}}
            showTooltip={showTooltip}
            loading={loading}
            skeletonCount={18}
            isFriendList={false}
            isFriendRequestList={false}
            isMatchRequestList={true}
          />
        )}

        {showGiftModal && selectedUserId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Chọn Quà Tặng</h2>
                <button
                  onClick={() => setShowGiftModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  X
                </button>
              </div>
              <SendGift
                selectedUserId={selectedUserId}
                onClose={() => setShowGiftModal(false)}
                onGiftSent={handleGiftSent}
              />
            </div>
          </div>
        )}

        {showPhotoGallery && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  Thư viện ảnh của {showPhotoGallery.name}
                </h2>
                <button
                  onClick={() => setShowPhotoGallery(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  X
                </button>
              </div>
              {showPhotoGallery.photos && showPhotoGallery.photos.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {showPhotoGallery.photos.map((photo, index) => (
                    <div key={index} className="relative">
                      <img
                        src={photo.photoUrl}
                        alt={`Gallery item ${index + 1} for ${showPhotoGallery.name}`}
                        className="w-full h-40 object-cover rounded-lg"
                        onError={(e) => (e.target.src = "https://placehold.co/150x150")}
                      />
                      {photo.caption && (
                        <p className="text-sm text-gray-600 mt-1">{photo.caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500">Không có ảnh nào để hiển thị.</p>
              )}
            </div>
          </div>
        )}

        {tooltip && (
          <div
            className={`fixed top-4 right-4 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2 ${
              isTooltipFading ? "animate-fade-out" : "animate-slide-in"
            } ${tooltip.type === "success" ? "bg-green-500" : tooltip.type === "info" ? "bg-blue-500" : "bg-red-500"}`}
          >
            <span>{tooltip.message}</span>
          </div>
        )}

        {chatUsers.length > 0 && (
          <ChatWidgetParticipants
            onSelectChat={chatUsers[chatUsers.length - 1]}
            onClose={() => setChatUsers([])}
            users={[]}
            friendsList={likes.concat(matches.map((m) => String(m.userId._id) === String(authUser._id) ? m.likedUserId : m.userId))}
          />
        )}
      </div>
    </div>
  );
};

export default LikePage;